/**
 * catchballSendTheBall — the first-toss orchestration behind
 * RequestComposerPage (desktop and phone).
 *
 * A toss is more than tossToMiddle: intent persists, the level-turn state
 * machine kicks off down the designer path spine, and one Ball is created and
 * sent per recipient. Since migration 744 all of that is ONE server
 * transaction — catchball_send_the_ball — so a failure part-way can no longer
 * leave a tossed loop with some or no Balls (build contract §2.3a). This
 * module builds the recipient list and maps the RPC result to the shape both
 * surfaces render; it performs no writes of its own.
 *
 * UI-free and dependency-injected: the RPC call arrives as a callback, so both
 * surfaces — and tests — drive the identical request.
 */

import type {
  CatchballLoop,
  LoopIntent,
  LoopIntentOstContext,
} from '@/types/prioritization/catchballLoop';
import { normalizeStringList } from '@/types/prioritization/catchballLoop';
import type { OrgUnit } from '@/types/prioritization/enterpriseIdentity';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';

// ─────────────────────────────────────────────────────────────────────────────
// Draft model + pure builders (moved from RequestComposerPage)
// ─────────────────────────────────────────────────────────────────────────────

export type ComposerDraft = {
  strategicContext: string;
  asks: string[];
  constraints: string[];
  guardrails: string[];
  responseGuidance: string;
  dueDate: string;
  maxIterations: number | null;
  expectedResponseType: LoopIntent['expectedResponseType'];
  recipients: string[];
  recipientOrgUnitIds: string[];
  ostTreeId: string;
  ostTreeName: string;
  inScopeItemTypes: string[];
  proposalCountGuidance: string;
  evidenceExpectations: string[];
  criteriaGovernance: 'imposed' | 'team-defined';
};

export function getRecipients(
  loop: CatchballLoop | null,
  orgUnitsMap?: Map<string, OrgUnit>,
): string[] {
  if (!loop) return [];
  const ids = loop.intent?.recipientOrgUnitIds;
  if (ids?.length && orgUnitsMap) {
    return ids.map((id) => orgUnitsMap.get(id)?.displayName ?? id).filter(Boolean);
  }
  const labels = (loop.levelConfig?.middle ?? [])
    .map((participant) => participant.orgUnitId || participant.userId)
    .filter(Boolean);
  // Never invent a placeholder recipient — an empty list must stay empty so
  // readiness checks and the setup-kickoff UI can tell "none configured" from
  // "configured". Confirm-dialog copy handles the empty case separately.
  return labels;
}

export function buildDraft(
  loop: CatchballLoop | null,
  orgUnitsMap?: Map<string, OrgUnit>,
): ComposerDraft {
  return {
    strategicContext: loop?.intent?.strategicContext ?? '',
    asks: loop?.intent?.asks?.length ? loop.intent.asks : [''],
    constraints: normalizeStringList(loop?.intent?.constraints, ';'),
    guardrails: loop?.intent?.guardrails ?? [],
    responseGuidance: loop?.intent?.responseGuidance ?? '',
    dueDate: loop?.intent?.dueDate ?? '',
    // null = unlimited. Owner ruling 2026-07-08: never default to an iteration cap.
    maxIterations: loop?.intent?.maxIterations ?? null,
    expectedResponseType: loop?.intent?.expectedResponseType ?? 'options',
    recipients: getRecipients(loop, orgUnitsMap),
    recipientOrgUnitIds: loop?.intent?.recipientOrgUnitIds ?? [],
    ostTreeId: loop?.intent?.ostContext?.treeId ?? '',
    ostTreeName: loop?.intent?.ostContext?.treeName ?? '',
    inScopeItemTypes: loop?.intent?.inScopeItemTypes ?? [],
    proposalCountGuidance: loop?.intent?.proposalCountGuidance ?? '',
    evidenceExpectations: loop?.intent?.evidenceExpectations ?? [],
    criteriaGovernance: loop?.intent?.criteriaGovernance ?? 'team-defined',
  };
}

export function buildIntentPayload(
  draft: ComposerDraft,
  existingIntent?: LoopIntent,
  ostContext?: LoopIntentOstContext,
): Partial<LoopIntent> {
  return {
    ...existingIntent,
    strategicContext: draft.strategicContext.trim(),
    asks: draft.asks.map((ask) => ask.trim()).filter(Boolean),
    constraints: draft.constraints,
    guardrails: draft.guardrails,
    responseGuidance: draft.responseGuidance.trim(),
    dueDate: draft.dueDate || undefined,
    maxIterations: draft.maxIterations,
    expectedResponseType: draft.expectedResponseType,
    recipientOrgUnitIds:
      draft.recipientOrgUnitIds.length > 0 ? draft.recipientOrgUnitIds : undefined,
    ostContext,
    inScopeItemTypes: draft.inScopeItemTypes.length > 0 ? draft.inScopeItemTypes : undefined,
    proposalCountGuidance: draft.proposalCountGuidance.trim() || undefined,
    evidenceExpectations:
      draft.evidenceExpectations.length > 0 ? draft.evidenceExpectations : undefined,
    criteriaGovernance: draft.criteriaGovernance,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The toss — one server transaction (migration 744, catchball_send_the_ball)
// ─────────────────────────────────────────────────────────────────────────────

/** One entry of catchball_send_the_ball's p_recipients. */
export interface SendTheBallRecipient {
  org_unit_id?: string;
  org_unit_name?: string;
  recipient_user_id?: string;
  recipient_user_name?: string;
  request_payload?: {
    name?: string;
    request_text?: string;
    request_instructions?: string;
    request_constraints?: string;
    due_date?: string;
  };
}

export interface SendTheBallRpcArgs {
  loopId: string;
  intent: LoopIntent;
  target: 'middle' | 'bottom';
  recipients: SendTheBallRecipient[];
  actorId: string;
}

/** catchball_send_the_ball's jsonb result (a transport error arrives as `error`). */
export interface SendTheBallRpcResult {
  success: boolean;
  loop_id?: string;
  turn_id?: string | null;
  balls?: Array<{ id: string; recipient: string; index?: number }>;
  loop?: Record<string, unknown>;
  error?: string;
  step?: string;
  invalid_recipients?: Array<{ index: number; recipient: string; reason: string }>;
}

export interface SendTheBallActions {
  /**
   * Calls the catchball_send_the_ball RPC — intent, level-turn kickoff, toss
   * and every Ball in ONE transaction; all-or-nothing. Wire to the loop
   * store's `sendTheBall`, which also applies the authoritative loop row.
   */
  sendTheBall: (args: SendTheBallRpcArgs) => Promise<SendTheBallRpcResult>;
}

export interface SendTheBallInput {
  loop: CatchballLoop;
  cycleId: string;
  userId: string;
  /** The full intent to persist and toss — build with buildIntentPayload. */
  intent: LoopIntent;
  draft: ComposerDraft;
  catchballSetupMode: 'org_chart' | 'virtual';
  /** Virtual-mode recipients from CatchballVirtualConfig (ignored in org mode). */
  virtualBallRecipients: CatchballParticipantRef[];
  orgUnits: Map<string, OrgUnit>;
  workspaceId?: string | null;
  actions: SendTheBallActions;
}

export interface SendTheBallFailure {
  /** Display name of the recipient. */
  recipient: string;
  reason: string;
}

export interface SendTheBallResult {
  /** True only when the toss went through and every recipient got a sent Ball. */
  ok: boolean;
  /**
   * Always false since 744: the send is one transaction, so either all of it
   * happened or none of it did. Kept for older callers.
   */
  partial?: boolean;
  error?: string;
  /** Balls created and sent (same as `sent`; kept for older callers). */
  ballCount: number;
  sent: number;
  /** Recipients attempted. */
  total: number;
  /** Recipients the server refused — when non-empty, nothing was sent. */
  failed: SendTheBallFailure[];
  /** Names of the recipients whose Ball was sent. */
  recipientNames: string;
}

/** One user-facing sentence for a non-ok result. */
export function describeSendFailures(result: SendTheBallResult): string {
  const list = result.failed.map((f) => `${f.recipient} (${f.reason})`).join('; ');
  if (result.total === 0 || !list) return result.error ?? '';
  if (result.sent === 0) {
    return `Nothing was sent — fix these recipients and send again: ${list}.`;
  }
  return `Sent to ${result.sent} of ${result.total} — these did not go: ${list}.`;
}

/** The recipients the composer sends to, in catchball_send_the_ball's p_recipients shape. */
export function buildSendRecipients(input: {
  draft: ComposerDraft;
  catchballSetupMode: 'org_chart' | 'virtual';
  virtualBallRecipients: CatchballParticipantRef[];
  orgUnits: Map<string, OrgUnit>;
}): SendTheBallRecipient[] {
  const { draft, catchballSetupMode, virtualBallRecipients, orgUnits } = input;
  const request = {
    request_text: draft.responseGuidance || draft.ostTreeName || undefined,
    request_instructions: draft.responseGuidance || undefined,
    request_constraints: draft.constraints.length > 0 ? draft.constraints.join('; ') : undefined,
    due_date: draft.dueDate || undefined,
  };
  if (catchballSetupMode === 'virtual' && virtualBallRecipients.length > 0) {
    return virtualBallRecipients.map((ref): SendTheBallRecipient => {
      const request_payload = { name: `${ref.displayName} Ball`, ...request };
      return ref.type === 'user'
        ? { recipient_user_id: ref.id, recipient_user_name: ref.displayName, request_payload }
        : { org_unit_name: ref.displayName, request_payload };
    });
  }
  return draft.recipientOrgUnitIds.map((orgUnitId) => {
    const displayName = orgUnits.get(orgUnitId)?.displayName ?? orgUnitId;
    return {
      org_unit_id: orgUnitId,
      org_unit_name: displayName,
      request_payload: { name: `${displayName} Ball`, ...request },
    };
  });
}

function recipientLabel(r: SendTheBallRecipient | undefined): string {
  if (!r) return '';
  return r.org_unit_name || r.recipient_user_name || r.org_unit_id || r.recipient_user_id || '';
}

const EMPTY = { ballCount: 0, sent: 0, recipientNames: '' } as const;

export async function sendTheBall(input: SendTheBallInput): Promise<SendTheBallResult> {
  const recipients = buildSendRecipients(input);
  const total = recipients.length;

  let rpc: SendTheBallRpcResult;
  try {
    rpc = await input.actions.sendTheBall({
      loopId: input.loop.id,
      intent: input.intent,
      target: 'middle',
      recipients,
      actorId: input.userId,
    });
  } catch (err) {
    rpc = { success: false, error: err instanceof Error ? err.message : String(err) };
  }

  if (!rpc.success) {
    const invalid = rpc.invalid_recipients ?? [];
    if (invalid.length > 0) {
      // One or more recipients are not valid on the server: nothing was sent.
      const result: SendTheBallResult = {
        ok: false,
        partial: false,
        ...EMPTY,
        total,
        failed: invalid.map((i) => ({
          recipient: i.recipient || recipientLabel(recipients[i.index]),
          reason: i.reason,
        })),
      };
      result.error = describeSendFailures(result);
      return result;
    }
    // Refused as a whole (possession, loop state, turn, transport): nothing changed.
    return {
      ok: false,
      partial: false,
      error:
        rpc.error || 'Requests could not be sent. Take the ball first if prompted, then try again.',
      ...EMPTY,
      total: 0,
      failed: [],
    };
  }

  const balls = rpc.balls ?? [];
  const recipientNames =
    total > 0
      ? balls.map((b) => b.recipient).filter(Boolean).join(', ')
      : input.draft.recipients.length > 0
        ? input.draft.recipients.join(', ')
        : 'the next org level';
  return {
    ok: true,
    partial: false,
    ballCount: balls.length,
    sent: balls.length,
    total,
    failed: [],
    recipientNames,
  };
}
